import React from "react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-kingdom-gold font-semibold uppercase tracking-widest text-sm">Public Policy</p>
          <h1 className="font-heading text-4xl md:text-5xl mt-3">Privacy Policy</h1>
          <p className="mt-4 text-slate-200">The Kingdom of Yunah'Shach'Qal Yashar'al · gov.yasharal.vip</p>
          <p className="mt-2 text-sm text-slate-300">Effective: August 31, 2026</p>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10 leading-7">
        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">1. Scope</h2>
          <p>
            This Privacy Policy explains how gov.yasharal.vip and Nation Studio collect, use, protect, retain,
            and disclose information when people use the public website, Nation portal, communications tools,
            live-streaming features, recordings, and connected social-media destinations.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">2. Information we may collect</h2>
          <p>
            Depending on how the site is used, information may include account identifiers, contact information,
            application information, Nation roles and access permissions, communications metadata, uploaded files,
            recordings, live-session information, and technical information needed for security and operation.
            Sensitive portal information may be encrypted at rest where the feature supports it.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">3. Social-account connections and Google/YouTube data</h2>
          <p>
            Nation Studio may allow a user to connect a YouTube or other supported social-media account. Social
            passwords are entered only on the provider's own authorization page. Nation Studio does not ask for or
            store a user's Google, YouTube, Facebook, or other social-media password.
          </p>
          <p className="mt-3">
            When a user authorizes Google/YouTube access, Nation Studio may receive OAuth access and refresh tokens,
            YouTube channel identifiers, channel names, and information necessary to create, manage, start, or stop
            live broadcasts selected by that user. OAuth credentials are stored as protected server-side secrets and
            are not exposed to other Nation users.
          </p>
          <p className="mt-3">
            Google user data obtained through Google APIs is used only to provide and improve the user-requested
            Nation Studio connection and live-streaming functionality. We do not sell Google user data, use it for
            advertising profiles, or transfer it to unrelated third parties. Our use and transfer of information
            received from Google APIs will adhere to the Google API Services User Data Policy, including its Limited
            Use requirements.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">4. How information is used</h2>
          <p>
            Information is used to authenticate users, enforce Nation access permissions, operate meetings and live
            broadcasts, deliver requested recordings and files, administer connected streaming destinations, provide
            support, maintain security, prevent abuse, and keep appropriate operational and audit records.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">5. Communications, meetings, and recordings</h2>
          <p>
            Some Nation meetings are private and some broadcasts are intentionally public. A host may choose to record
            a session. Participants should be clearly informed when recording or public broadcasting is active.
            Private-room communications may use end-to-end encryption where that feature is enabled; recordings and
            public program outputs are separate outputs intentionally created by the host and therefore are not treated
            as private room ciphertext once they are recorded or broadcast.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">6. Sharing and service providers</h2>
          <p>
            Information may be processed by infrastructure providers that host the website, database, media services,
            email delivery, object storage, or payment functions. A user-requested public stream may also be sent to
            the social platforms that user selects. We do not sell personal information to advertisers.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">7. Security</h2>
          <p>
            We use access controls, server-side secret management, encrypted transport, protected authentication
            cookies, CSRF protections, application-level encryption for selected sensitive records, and other
            safeguards appropriate to the feature. No online system can guarantee absolute security.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">8. Retention and deletion</h2>
          <p>
            Information is retained only as long as reasonably necessary for Nation administration, the requested
            service, security, legal obligations, or legitimate recordkeeping. A user may request deletion of eligible
            personal information or disconnection of a connected social account by contacting admin@yasharal.vip.
            Disconnecting a social destination prevents future Nation Studio use of that connection.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">9. Revoking Google access</h2>
          <p>
            A user can disconnect YouTube inside Nation Studio and can also revoke the application's Google access
            from the security/permissions area of the user's Google Account. Revocation prevents new API access with
            that authorization. Previously created public broadcasts or recordings may remain on the destination
            platform according to that platform's own settings and policies.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">10. Children and youth accounts</h2>
          <p>
            Youth access to Nation systems is governed by Nation household, parental, and administrative rules.
            Public social-account connections and producer controls should be used only by people authorized to manage
            the relevant account or destination.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">11. Changes</h2>
          <p>
            This policy may be updated as Nation Studio and related services change. The effective date above will be
            updated when material revisions are published.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl text-royal-blue mb-3">12. Contact</h2>
          <p>
            Questions or privacy requests may be sent to <a className="text-royal-blue underline" href="mailto:admin@yasharal.vip">admin@yasharal.vip</a>.
          </p>
        </section>

        <div className="pt-6 border-t border-slate-200 flex flex-wrap gap-4 text-sm">
          <Link className="text-royal-blue underline" to="/terms">Terms of Service</Link>
          <Link className="text-royal-blue underline" to="/contact">Contact</Link>
          <Link className="text-royal-blue underline" to="/">Home</Link>
        </div>
      </main>
    </div>
  );
}
